import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight } from 'lucide-react';
export const Route = createFileRoute('/_layout/blog/qr-best-practices/')({
    component: QrBestPracticesPost,
    head: () => ({
        meta: [
            { title: 'QR best practices for restaurants and retail — Adora' },
            {
                name: 'description',
                content: 'Where to place QR codes, what to write next to them, and why scan counts ≠ marketing insight. Field-tested patterns from Cambodian SMEs.',
            },
            { property: 'og:title', content: 'QR best practices for restaurants and retail — Adora' },
            {
                property: 'og:description',
                content: 'Where to place QR codes, what to write next to them, and why scan counts ≠ marketing insight. Field-tested patterns from Cambodian SMEs.',
            },
        ],
    }),
});
function QrBestPracticesPost() {
    return (<article className="mx-auto max-w-3xl px-6 md:px-8 py-12">
            <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-3.5 w-3.5"/>
                All posts
            </Link>

            <header className="mt-6 space-y-3">
                <p className="text-xs uppercase tracking-wide text-primary">Guide · 2026-04-28</p>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                    QR best practices for restaurants and retail
                </h1>
                <p className="text-lg text-muted-foreground leading-relaxed">
                    Field-tested patterns from running Adora across Cambodian SMEs. Skip the obvious, this is the stuff
                    that moves conversion from 5% to 25%.
                </p>
            </header>

            <div className="prose prose-neutral dark:prose-invert mt-10 max-w-none">
                <h2>1. Tell people what's behind the code</h2>
                <p>
                    The single biggest predictor of scan-through rate isn't the design of the QR — it's the
                    <strong> 4–6 word call-to-action next to it</strong>. "Scan to order", "Scan for today's special",
                    "Scan to get the WhatsApp link". A naked QR with no context converts at 2–4%. A QR with a clear
                    promise converts at 15–30%.
                </p>

                <h2>2. One QR per physical placement</h2>
                <p>
                    Give each surface its own <strong>Smart Link</strong> in Adora. The same campaign can have a QR for
                    the table tent, one for the menu insert, one for the packaging, one for the tuk-tuk. Each one is its
                    own scan source — when one converts 4× the others, you know to lean in.
                </p>
                <p>
                    The temptation is to print one QR everywhere because it's simpler. The cost: you'll never know which
                    surface earned the customer. Adora's whole point is letting you tell.
                </p>

                <h2>3. Test placement, not just creative</h2>
                <p>
                    Run the same QR design at <strong>two different heights</strong> on a tuk-tuk (waist vs. eye-level).
                    Run the same restaurant menu QR <strong>on the front cover vs. the inside back</strong>. The
                    creative-design industry obsesses over the QR style; field data says <em>position</em> is the bigger
                    lever.
                </p>

                <h2>4. Bot scans are real — exclude them</h2>
                <p>
                    Facebook, LinkedIn, WhatsApp, Telegram, and Apple's link-preview crawlers all hit your QR's landing
                    URL when someone shares it. By default Adora records these (so you can see them) but excludes them
                    from KPIs. The dashboard's "Browser / app" filter has <code>bot</code> selected by default — leave
                    it on unless you specifically want to debug crawler hits.
                </p>

                <h2>5. Use Auto-store for app campaigns</h2>
                <p>
                    If your QR's job is "install the app", don't ship users to a generic landing page. Use
                    <strong> Auto-store mode</strong> — Adora sniffs the OS and routes iOS to the App Store, Android to
                    Play, desktop to your fallback. You get one QR, three correctly-attributed paths, and the same scan
                    record carries the click for analytics.
                </p>

                <h2>6. Add a feedback campaign on the receipt</h2>
                <p>
                    The single best place for a feedback QR is the receipt or the to-go bag — the moment immediately
                    after the experience, while the customer still feels something. Use Adora's
                    <strong> Feedback mode</strong> with a 1-tap rating. Your average rating is more honest than any
                    Google review because it captures the silent middle, not just the loudest 10%.
                </p>

                <h2>What "good" looks like</h2>
                <ul>
                    <li>Scan-through rate (non-bot): &gt; 8% of unique impressions</li>
                    <li>Click-through on landing (multi-link campaigns): &gt; 60% of scans</li>
                    <li>Feedback submission rate: 3–8% of feedback-mode scans</li>
                    <li>Average rating: 4.2+ in restaurants, 4.0+ in retail</li>
                </ul>
                <p>
                    Below those numbers? The problem is almost always the call-to-action or the placement, not the QR
                    design.
                </p>
            </div>

            <footer className="mt-12 border-t pt-6 flex items-center justify-between">
                <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-3.5 w-3.5"/>
                    All posts
                </Link>
                <Link to="/sign-up" search={{ redirect: '/console' }} className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90">
                    Try Adora free
                    <ArrowRight className="h-4 w-4"/>
                </Link>
            </footer>
        </article>);
}
